import {
  clearStorageFavoritePageIds,
  getStorageFavoritePageIds,
} from '@sportworld/js-sdk/src/actions/favorites'
import { setUserSelectedRegionId } from '@sportworld/js-sdk/src/actions/region'
import { getStorageSettings } from '@sportworld/js-sdk/src/actions/settings'
import {
  getAnonymousUser,
  getRegisteredUser,
} from '@sportworld/js-sdk/src/actions/user'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { FavoritesService } from '@sportworld/js-sdk/src/service/favorites'
import { TileDataType } from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'
import { ReducedPageType } from '@sportworld/js-sdk/src/types/servicesTypes/pageTypes'
import { OptaTeamStanding } from '@sportworld/js-sdk/src/types/soccerDataTypes'
import { createTeamPage } from '@sportworld/js-sdk/src/utils/soccerData'
import { getPageParams } from '@sportworld/js-sdk/src/utils/utils'

import { getReducedFavoritePages } from '@/actions/pages'
import {
  getRegionById,
  setActiveRegionData,
  getPrivacyRegulation,
} from '@/actions/region'
import { eventCategory } from '@/constants/analytics'
import { defaultAppConfig, fetchStatus, pageTypes } from '@/constants/common'
import { focusIds } from '@/constants/focus'
import { paths } from '@/constants/paths'
import { popupStatus } from '@/constants/popup'
import { StorageIds } from '@/constants/storage'
import { ucInit } from '@/containers/Usercentrics/usercentricsUtils'
import { OpenTileProps } from '@/hooks/useOpenTile'
import i18n from '@/i18n'
import { deepLinkToPromotion, getModelName } from '@/platform/platform'
import { ApiService, PageService } from '@/service'
import useStore from '@/store/useStore'
import {
  AppSettingsType,
  ListPopupType,
  MediaContentType,
  StageSlide,
  StageSlideType,
  TileType,
  tileTypes,
} from '@/types/servicesTypes/configTypes'
import {
  BatchContextType,
  CataloguePagesType,
  FillRatePerContextType,
  PageOptionsType,
} from '@/types/servicesTypes/pageTypes'
import { HasPromotionType } from '@/types/utilsTypes'
import {
  addThemeForPage,
  getHomeTabPageParams,
  isTVOS,
  sendEventToGTAG,
  showFetchTimeInfo,
  showInfo,
} from '@/utils/utils'

const {
  setAppConfig,
  setAppConfigStatus,
  setCurrentEvent,
  setMediaVideo,
  setCataloguePages,
  setAllChannels,
  setAppSettings,
  setCalendar,
  setUsercentricsPopup,
  setIsLoading,
  setHasError,
  setConsentStatus,
} = useStore.getState().common

const {
  setOffers,
  setHasPromotion,
  setLoading: setPromotionLoading,
  setError: setPromotionError,
} = useStore.getState().promotion
const { setPage, setLoading } = useStore.getState().page
const { setPagePopup, setPagePopupStatus } = useStore.getState().popup
const {
  addFavoritePageStore,
  initFavoritePagesStore,
  removeFavoritePageStore,
} = useStore.getState().favoritePage

export const updateAppConfig = async (regionId?: string): Promise<void> => {
  const start = performance.now()
  let appConfigRegionId = 'default'

  const fetchAppConfig = async (fetchingRegionId?: string) => {
    const appConfig = await ApiService.getAppConfig(fetchingRegionId)
    setAppConfig({ ...defaultAppConfig, ...appConfig })
    appConfigRegionId = appConfig.regionId || 'default'
    isDevMode &&
      showInfo(
        `regionId ${fetchingRegionId} • activeRegionId ${fetchingRegionId} • appConfigRegionId ${appConfigRegionId}`,
      )
  }
  const catchHandler = () => {
    setAppConfig(defaultAppConfig)
    showInfo(i18n.t('toasts.configIsNotAccessible'))
  }
  try {
    await fetchAppConfig(regionId)
    const end = performance.now()

    showFetchTimeInfo(`Update appconfig`, start, end)
  } catch {
    if (regionId) {
      try {
        await fetchAppConfig()
      } catch {
        catchHandler()
      }
    } else {
      catchHandler()
    }
  }
  const { countryCode, privacyRegulation } = await getRegionById(
    appConfigRegionId,
  )
  setActiveRegionData(appConfigRegionId, countryCode, privacyRegulation)
  setUserSelectedRegionId(appConfigRegionId)
  setAppConfigStatus(fetchStatus.COMPLETED)
}

export const updateUcStatus = async (userCentricsId: string) => {
  const privacyRegulation = getPrivacyRegulation()
  if (privacyRegulation === 'None') {
    return setConsentStatus(fetchStatus.COMPLETED)
  }
  try {
    await ucInit(userCentricsId)
    const ucUserInteraction = localStorage.getItem(StorageIds.ucUserInteraction)
    if (ucUserInteraction === 'false') {
      setUsercentricsPopup(true)
    } else {
      setConsentStatus(fetchStatus.COMPLETED)
    }
  } catch {
    console.error('[Usercentrics] init error')
    setUsercentricsPopup(false)
    setConsentStatus(fetchStatus.COMPLETED)
  }
}

export const initFavorites = async () => {
  try {
    const storageFavoritePageIds = getStorageFavoritePageIds()
    const res = await FavoritesService.getFavoritePageIds()
    const pageIds = storageFavoritePageIds
      ? Array.from(new Set([...storageFavoritePageIds, ...res]))
      : res

    if (storageFavoritePageIds) {
      await updateFavoritePages(pageIds)
      clearStorageFavoritePageIds()
    }

    const reducedPages = await getReducedFavoritePages(pageIds)
    initFavoritePagesStore(reducedPages || {})
    return reducedPages || {}
  } catch {
    showInfo(i18n.t('toasts.favoriteIsUnavailable'))
    initFavoritePagesStore(null)
    return null
  }
}

export const addFavoritePage = async (page: ReducedPageType) => {
  try {
    await FavoritesService.addFavoritePageId(page.id)
    addFavoritePageStore(page)
  } catch (e) {
    return Promise.reject(e)
  }
}

export const removeFavoritePage = async (pageId: string) => {
  try {
    await FavoritesService.removeFavoritePageId(pageId)
    removeFavoritePageStore(pageId)
  } catch (e) {
    return Promise.reject(e)
  }
}

export const updateFavoritePages = async (pageIds: Array<string>) => {
  try {
    await FavoritesService.updateFavoritePageIds(pageIds)
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getPageById = async (
  pageId: string,
  abortSignal?: AbortSignal,
) => {
  try {
    const start = performance.now()
    setLoading(true)

    const page = await PageService.getPageById(pageId, abortSignal)

    if (!page) {
      return console.error('Page not found or operation aborted')
    }
    const isHomeTab = page.pageType === pageTypes.HOME_TAB

    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(page, pageId)
      : getPageParams({ pageId, tabs: page.tabs })

    setPage({ ...page, ...pageIdParams })

    const end = performance.now()
    showFetchTimeInfo(`Page ${page?.name}`, start, end)
  } catch (e) {
    return Promise.reject(e)
  } finally {
    setLoading(false)
  }
}

export const getPagePopupById = async (
  pageId: string,
  {
    listOptions,
    pageOptions,
    abortSignal,
    skipLoadingStatus,
  }: {
    listOptions?: ListPopupType
    pageOptions?: PageOptionsType
    abortSignal?: AbortSignal
    skipLoadingStatus?: boolean
  } = {},
) => {
  const pagePopup = useStore.getState().popup.pagePopup
  try {
    skipLoadingStatus || setPagePopupStatus(popupStatus.LOADING)
    const page = await PageService.getPageById(pageId, abortSignal)
    if (!page) {
      await Promise.reject('no page found')
    }

    const isHomeTab = page.pageType === pageTypes.HOME_TAB
    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(page, pageId)
      : getPageParams({ pageId, tabs: page.tabs })

    setPagePopup({
      ...page,
      ...pageIdParams,
      listOptions,
      pageOptions,
    })
    setPagePopupStatus(popupStatus.OPEN)
  } catch (e) {
    setPagePopupStatus(pagePopup ? popupStatus.OPEN : popupStatus.CLOSED)
    return console.error(e)
  }
}

export const getPagePopupByTeamId = async (
  team: OptaTeamStanding,
  tournamentCalendarId?: string,
  abortSignal?: AbortSignal,
  pageOptions?: PageOptionsType,
) => {
  const teamId = team.contestantId
  const { pagePopup, setPagePopupSelectedTeamId } = useStore.getState().popup
  const { page } = useStore.getState().page
  try {
    setPagePopupStatus(popupStatus.LOADING)

    const fetchedPage = await ApiService.getPageByTeamId(teamId, abortSignal)
    const teamPage = fetchedPage || createTeamPage(team, tournamentCalendarId)
    const pageWithTheme = addThemeForPage(teamPage, pagePopup || page)

    setPagePopupSelectedTeamId(teamId)
    setPagePopup({ ...pageWithTheme, pageOptions })
    setPagePopupStatus(popupStatus.OPEN)
  } catch (e) {
    setPagePopupStatus(pagePopup ? popupStatus.OPEN : popupStatus.CLOSED)
    return Promise.reject(e)
  }
}

export const getHomePage = async (search?: string) => {
  const { appConfig } = useStore.getState().common
  const pageData = appConfig?.home
  const isHomeTab = pageData?.pageType === pageTypes.HOME_TAB

  if (pageData) {
    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(pageData, search)
      : getPageParams({ pageId: search, tabs: pageData.tabs })

    setPage({ ...pageData, ...pageIdParams })
  }
}

export const getCalendarClassic = async () => {
  setIsLoading({ calendar: true })
  setHasError({ calendar: false })
  const start = performance.now()
  try {
    const res = await ApiService.getCalendarClassic()
    setCalendar(res)
    const end = performance.now()
    showFetchTimeInfo(`Calendar`, start, end)
  } catch (e) {
    setHasError({ calendar: true })
    return Promise.reject(e)
  } finally {
    setIsLoading({ calendar: false })
  }
}

export const getSpecialHomePage = async (
  abortSignal?: AbortSignal,
  search?: string,
) => {
  const { appConfig } = useStore.getState().common
  const { specialHomePageId, specialHome } = appConfig

  if (specialHome) {
    const isHomeTab = specialHome?.pageType === pageTypes.HOME_TAB

    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(specialHome, search)
      : getPageParams({ pageId: search, tabs: specialHome.tabs })
    return setPage({ ...specialHome, ...pageIdParams })
  }
  if (!specialHomePageId) {
    return console.error('Special Home page not found')
  }

  try {
    setLoading(true)
    const start = performance.now()
    const specialHomePage = await PageService.getPageById(
      specialHomePageId,
      abortSignal,
    )

    setAppConfig({ ...appConfig, specialHome: specialHomePage })
    const isHomeTab = specialHomePage?.pageType === pageTypes.HOME_TAB

    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(specialHomePage, search)
      : getPageParams({ pageId: search, tabs: specialHomePage.tabs })
    setPage({ ...specialHomePage, ...pageIdParams })

    const end = performance.now()
    showFetchTimeInfo('Special Home', start, end)
  } catch (e) {
    return Promise.reject(e)
  } finally {
    setLoading(false)
  }
}

export const getSelectedPage = async (
  abortSignal: AbortSignal,
  search?: string,
) => {
  const { appConfig } = useStore.getState().common
  const pageData = appConfig?.selected

  if (pageData) {
    const isHomeTab = pageData.pageType === pageTypes.HOME_TAB

    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(pageData, search)
      : getPageParams({ pageId: search, tabs: pageData.tabs })

    setPage({ ...pageData, ...pageIdParams })
    return
  }

  try {
    setLoading(true)
    const start = performance.now()
    const selectedPage = await ApiService.getSelectedPage(abortSignal)

    setAppConfig({ ...appConfig, selected: selectedPage })
    const isHomeTab = selectedPage?.pageType === pageTypes.HOME_TAB
    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(selectedPage, search)
      : getPageParams({ pageId: search, tabs: selectedPage.tabs })

    setPage({ ...selectedPage, ...pageIdParams })
    const end = performance.now()
    showFetchTimeInfo(`Selected`, start, end)
  } catch (e) {
    // showError('Selected page is not accessible')
    return Promise.reject(e)
  } finally {
    setLoading(false)
  }
}

export const getFocusPage = async (
  abortSignal?: AbortSignal,
  search?: string,
) => {
  const { appConfig } = useStore.getState().common
  const { focusPageId, focusPage } = appConfig

  if (focusPage) {
    const isHomeTab = focusPage?.pageType === pageTypes.HOME_TAB

    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(focusPage, search)
      : getPageParams({ pageId: search, tabs: focusPage.tabs })
    return setPage({ ...focusPage, ...pageIdParams })
  }
  if (!focusPageId) {
    return console.error('FocusPage page not found')
  }

  try {
    setLoading(true)
    const start = performance.now()
    const focusPage = await PageService.getPageById(focusPageId, abortSignal)

    setAppConfig({ ...appConfig, focusPage })
    const isHomeTab = focusPage?.pageType === pageTypes.HOME_TAB

    const pageIdParams = isHomeTab
      ? getHomeTabPageParams(focusPage, search)
      : getPageParams({ pageId: search, tabs: focusPage.tabs })
    setPage({ ...focusPage, ...pageIdParams })

    const end = performance.now()
    showFetchTimeInfo('FocusPage', start, end)
  } catch (e) {
    return Promise.reject(e)
  } finally {
    setLoading(false)
  }
}

export const getSportsPage = async () => {
  const { appConfig } = useStore.getState().common
  const pageData = appConfig?.sportsPage
  if (pageData) {
    setPage(pageData)
    return
  }

  if (!appConfig.sportsPageId) {
    return console.error('Sports page not found')
  }

  try {
    setLoading(true)
    const start = performance.now()
    const sportsPage = await PageService.getPageById(appConfig.sportsPageId)

    setAppConfig({ ...appConfig, sportsPage: sportsPage })
    setPage(sportsPage)
    const end = performance.now()
    showFetchTimeInfo(`SportsPage`, start, end)
  } catch (e) {
    // showError('Selected page is not accessible')
    return Promise.reject(e)
  } finally {
    setLoading(false)
  }
}

export const getBatchGridData = async (
  contextIds: Array<string>,
  includeIfEmptyContextIds: Array<string>,
  limitSpacesPerGrid: number,
  minFillRatePerContext: Array<FillRatePerContextType>,
  utcOffsetMins: number,
  layout: string,
  abortSignal?: AbortSignal,
) => {
  try {
    const batchData: BatchContextType = {
      contextIds,
      includeIfEmptyContextIds,
      limitSpacesPerGrid,
      minFillRatePerContext,
      utcOffsetMins,
    }

    const res = await ApiService.getBatchGridData(
      batchData,
      layout,
      abortSignal,
    )
    return res?.grids
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getCataloguePages = async (limit: number) => {
  const { appConfig } = useStore.getState().common
  const pagesData = appConfig?.catalogue || []

  const catalogue: CataloguePagesType = {
    next: 'string',
    totalCount: -1,
    items: pagesData,
  }

  if (pagesData) {
    setCataloguePages(catalogue)
    return
  }

  /**
   * Fallback if appConfig not available
   */
  try {
    const res = await ApiService.getCataloguePages(limit)

    setCataloguePages(res)
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getBatchLinesData = async (
  contextIds: Array<string>,
  includeIfEmptyContextIds: Array<string>,
  limitTilesPerLine: number,
  utcOffsetMins?: number,
  abortSignal?: AbortSignal,
) => {
  try {
    const batchRequestData: BatchContextType = {
      contextIds,
      includeIfEmptyContextIds,
      limitTilesPerLine,
      utcOffsetMins,
    }

    const res = await ApiService.getBatchLinesData(
      batchRequestData,
      abortSignal,
    )
    return res?.lines
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getLineDataById = async (
  lineContextId: string,
  limitTilesPerLine: number,
  next?: string,
  utcOffsetMins?: number,
  abortSignal?: AbortSignal,
) => {
  try {
    const linesRequestData = {
      lineContextId,
      limitTilesPerLine,
      next,
      utcOffsetMins,
    }

    const res = await ApiService.getLineDataById(linesRequestData, abortSignal)
    return res
  } catch (e) {
    return Promise.reject(e)
  }
}

export const updateCurrentEventById = async (
  id: string,
  abortSignal?: AbortSignal,
) => {
  try {
    const res = await ApiService.getEventById(id, abortSignal)
    if (!res.categoryId) {
      const fallbackCategoryId = 'sport'
      res.categoryId = fallbackCategoryId
    }
    return setCurrentEvent(res)
  } catch (e) {}
}

export const updateAllChannels = async (limit?: number) => {
  const start = performance.now()
  try {
    const res = await ApiService.getAllChannels(limit)
    setAllChannels(res.items)
    const end = performance.now()
    showFetchTimeInfo(`Update All Channels`, start, end)
  } catch (e) {
    setAllChannels([])
  }
}
export const getChannelSources = async (channelId: string) => {
  try {
    const res = await ApiService.getChannelSources(channelId)
    return res
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getEventSources = async (eventId: string) => {
  try {
    const res = await ApiService.getEventSources(eventId)
    return res
  } catch (e) {
    return Promise.reject(e)
  }
}

export const toggleAppSettings = (settings: Partial<AppSettingsType>) => {
  setAppSettings(settings)
  setStorageSettings(settings)
}

export const updateAppSettings = () => {
  const appSettings = getStorageSettings()
  appSettings && setAppSettings(JSON.parse(appSettings))
}

const setStorageSettings = (data: Partial<AppSettingsType>) => {
  const appSettings = getStorageSettings()
  const patch = appSettings
    ? { ...JSON.parse(getStorageSettings()), ...data }
    : data
  localStorage.setItem(StorageIds.settings, JSON.stringify({ ...patch }))
}

export const initGTAG = () => {
  // if (isTVOS()) {
  ga_heartbeat()
  // }
}

const ga_heartbeat = () => {
  setInterval(() => {
    sendEventToGTAG(eventCategory.HEARTBEAT)
  }, 5 * 60 * 1000)
}

export const getRegisteredUserId = () => {
  const registeredUser = getRegisteredUser()
  try {
    return JSON.parse(registeredUser).userId
  } catch (error) {
    return ''
  }
}

export const getRegisteredUserEmail = () => {
  const registeredUser = getRegisteredUser()
  try {
    return JSON.parse(registeredUser).email
  } catch (error) {
    return ''
  }
}

export const getRegisteredUserPlenigoId = () => {
  const registeredUser = getRegisteredUser()
  try {
    return JSON.parse(registeredUser).userId
  } catch (error) {
    return ''
  }
}

export const getAnonymousUserId = () => {
  const anonymousUser = getAnonymousUser()
  try {
    return JSON.parse(anonymousUser).userId
  } catch (error) {
    return ''
  }
}

export const updateOffers = async () => {
  setPromotionLoading(true)
  setPromotionError(false)
  try {
    /**
     * There is a focusing issue on Tizen TV
     * Need to reset offers to rerender component from scratch
     * TODO: investigate focus handling
     * setOffers([])
     */
    const res = await ApiService.getOffers()
    const updatedOffers = res?.filter((offer) => !/Credit/.test(offer.id))
    setOffers(updatedOffers)
  } catch (e) {
    setOffers([])
    setPromotionError(true)
  } finally {
    setPromotionLoading(false)
  }
}

export const checkTvPromotions = async () => {
  // const model = 'GQ43Q60RGTXZG'
  const model = getModelName()
  if (model && isTVOS()) {
    const hasPromotion = await ApiService.getPromotionByModelCodeId(model)
    hasPromotion ? setHasPromotion(hasPromotion) : setHasPromotion(null)
  }
}

export const goToTvPromotion = async (promotion: HasPromotionType) => {
  try {
    promotion && deepLinkToPromotion(promotion)
  } catch (e) {}
}

export const openContentFromPreviewTile = (
  tile: TileType,
  navigate: (paths: string) => void,
  openEvent: (tileData: OpenTileProps) => void,
) => {
  const tileType = tile?.type
  const tileMediaVideo = tile?.data?.mediaData?.video || tile?.data?.media
  if (tileType === tileTypes.CHANNEL) {
    const channel = tile?.data?.channel

    if (channel) {
      const videoWithName: MediaContentType = {
        contentType: tile.type,
        name: channel.name,
        id: channel.id,
        channel,
      }
      setMediaVideo(videoWithName)
    }
  } else if (tileType === tileTypes.MEDIA || tileType === tileTypes.AD) {
    if (tileMediaVideo && tileMediaVideo.sources?.length) {
      const videoWithName: MediaContentType = {
        ...tileMediaVideo,
        contentType: tileType,
        name: tile.title,
        id: tileMediaVideo.id || tile.id,
        sources: tileMediaVideo.sources,
        onEnded: tileMediaVideo.onEnded,
        partnerIds: tile.data?.mediaData?.partnerIds,
      }
      setMediaVideo(videoWithName)
      navigate(paths.EMPTY)
    }
  } else if (tileType === tileTypes.EVENT) {
    openEvent({
      tileData: {
        id: tile.id,
        image: tile.image,
        type: tileTypes.EVENT,
        data: tile.data,
      },
      focusId: focusIds.CONTENT,
    })
    navigate(paths.EMPTY)
  } else if (tileType === tileTypes.PAGE) {
    if (tile?.data?.pageId) {
      void getPagePopupById(tile.data.pageId)
      navigate(paths.EMPTY)
    }
  }
}

export const openContentFromStageTile = (
  slide: StageSlide,
  openEvent: (tileData: OpenTileProps) => void,
) => {
  const slideType = slide?.type
  if (slideType === StageSlideType.EVENT) {
    openEvent({
      tileData: {
        id: slide.id,
        image: slide.image,
        type: tileTypes.EVENT,
        data: slide as Partial<TileDataType>,
      },
      focusId: focusIds.CONTENT,
    })
  } else if (slideType === StageSlideType.PAGE) {
    if (slide.pageLink) {
      void getPagePopupById(slide.pageLink)
    } else if (slide.page?.id) {
      void getPagePopupById(slide.page?.id)
    }
  } else if (slideType === StageSlideType.PROMOTION) {
    if (slide.promotion?.page?.id) {
      void getPagePopupById(slide.promotion?.page?.id)
    }
  }
}

export const getPromotions = async (
  partnerIds: string[],
  abortSignal?: AbortSignal,
) => {
  try {
    const res = await ApiService.getPromotions(partnerIds, abortSignal)
    return res
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getPromotionById = async (
  promotionId: string,
  abortSignal?: AbortSignal,
) => {
  try {
    const res = await ApiService.getPromotionById(promotionId, abortSignal)
    return res
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getPartnerPromotions = async (
  partnerId: string,
  abortSignal?: AbortSignal,
) => {
  try {
    const res = await ApiService.getPartnerPromotions(partnerId, abortSignal)
    return res
  } catch (e) {
    return Promise.reject(e)
  }
}

export const getPartnerPromotionById = async (
  partnerId: string,
  promotionId: string,
  abortSignal?: AbortSignal,
) => {
  try {
    const res = await ApiService.getPartnerPromotionById(
      partnerId,
      promotionId,
      abortSignal,
    )
    return res
  } catch (e) {
    return Promise.reject(e)
  }
}
